import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import HomePage from './pages/HomePage';
import VendorDetailPage from './pages/VendorDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MagicLinkRegisterPage from './pages/MagicLinkRegisterPage';
import CustomerDashboard from './pages/CustomerDashboard';
import VendorDashboard from './pages/VendorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import CaptainDashboard from './pages/CaptainDashboard';
import SocialConnectLanding from './pages/SocialConnectLanding';
import PublicPackagePage from './pages/PublicPackagePage';
import InstantPricingPage from './pages/InstantPricingPage';
import SEOListingPage from './pages/SEOListingPage';
import GlobalSEOMeta from './components/GlobalSEOMeta';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import DeleteMyAccountPage from './pages/DeleteMyAccountPage';
import CookieBanner from './components/CookieBanner';
import PermissionRationaleModal from './components/PermissionRationaleModal';
import VersionGate from './components/VersionGate';
import ImpersonationBanner from './components/ImpersonationBanner';
import { SentryErrorBoundary } from './sentry';
import './App.css';

const DashboardRedirect = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  if (user.role === 'customer') return <Navigate to="/dashboard/customer" replace />;
  if (user.role === 'vendor') return <Navigate to="/dashboard/vendor" replace />;
  if (user.role === 'professional') return <Navigate to="/dashboard/professional" replace />;
  if (user.role === 'captain') return <Navigate to="/dashboard/captain" replace />;
  if (user.role === 'admin') return <Navigate to="/dashboard/admin" replace />;
  
  return <Navigate to="/" replace />;
};

function App() {
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#FAF5EE]">
          <div className="max-w-md w-full bg-white border border-[#E8E1D5] rounded p-6 shadow-sm">
            <h1 className="font-heading text-2xl text-text-primary mb-2">Something went wrong</h1>
            <p className="text-sm text-text-secondary mb-4">
              We&apos;ve been notified and our team is looking into it. Please try again — most issues clear on a reload.
            </p>
            <button
              onClick={() => resetError()}
              className="bg-brand-primary text-sand-50 px-4 py-2 rounded-none text-sm font-bold hover:opacity-90"
              data-testid="sentry-error-reset-btn"
            >
              Try Again
            </button>
            {process.env.NODE_ENV !== 'production' && error?.message && (
              <pre className="mt-4 text-[10px] text-text-secondary overflow-x-auto opacity-60">{error.message}</pre>
            )}
          </div>
        </div>
      )}
      beforeCapture={(scope) => { scope.setTag('section', 'app-root'); }}
    >
    <VersionGate>
      <HelmetProvider>
      <GlobalSEOMeta />
      <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/vendors/:id" element={<VendorDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/p/:pkgId" element={<PublicPackagePage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/invite/:token" element={<MagicLinkRegisterPage />} />
          <Route path="/instant-pricing" element={<InstantPricingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/delete-my-account" element={<DeleteMyAccountPage />} />
          <Route path="/dashboard" element={<DashboardRedirect />} />
          <Route path="/dashboard/social-connect" element={<SocialConnectLanding />} />
          <Route
            path="/dashboard/customer"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/vendor"
            element={
              <ProtectedRoute allowedRoles={['vendor']}>
                <VendorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/professional"
            element={
              <ProtectedRoute allowedRoles={['professional']}>
                <ProfessionalDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/captain"
            element={
              <ProtectedRoute allowedRoles={['captain']}>
                <CaptainDashboard />
              </ProtectedRoute>
            }
          />
          {/* SEO landing pages — must come AFTER all named routes so we don't
              shadow /login, /register, /dashboard, /vendors/:id etc.
              Matches any /best-... or /top-... slug. */}
          <Route path="/:seoSlug" element={<SEOListingPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
        <CookieBanner />
        <PermissionRationaleModal />
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
    </VersionGate>
    </SentryErrorBoundary>
  );
}

export default App;
